import React, { useState } from 'react'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  Paper,
  Stepper,
  Step,
  StepLabel
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  NavigateNext as NavigateNextIcon,
  SkipNext as SkipNextIcon
} from '@mui/icons-material'
import toast from 'react-hot-toast'

const TeacherGhostTutorial = ({ userData, onBack }) => {
  const [currentStep, setCurrentStep] = useState(0)
  const [open, setOpen] = useState(true)

  const tutorialSteps = [
    {
      title: "Hoşgeldiniz Değerli Öğretmenim! 👻",
      content: "Benim adım Ghost, size başlangıç için yol arkadaşlığı yapacağım. Hazır mısınız?",
      highlight: null
    },
    {
      title: "Genel Bakış",
      content: "Bu kartlar ile sınıflarınızı yönetebilir, görevler oluşturabilir ve öğrencilerinizin sonuçlarını detaylı takip edebilirsiniz.",
      highlight: null
    },
    {
      title: "🚪 Sınıflarım Kartı",
      content: "Sınıflar oluşturabilir, onları görüntüleyebilir, öğrenci detaylarını inceleyebilir ve sınıf yönetimini yapabilirsiniz.",
      highlight: 'classes'
    },
    {
      title: "❓ Öğrenci Soruları",
      content: "Öğrencilerinizin sorularını görüp cevaplayabilirsiniz.",
      highlight: 'student-questions'
    },
    {
      title: "📄 Döküman Gönder",
      content: "Öğrencilerinize döküman gönderebilir ve paylaşabilirsiniz.",
      highlight: 'send-document'
    },
    {
      title: "📝 Mini Quiz Oluştur",
      content: "Hızlı quizler oluşturup öğrencilerinize gönderebilirsiniz.",
      highlight: 'mini-quiz'
    },
    {
      title: "📊 Quiz Sonuçları",
      content: "Öğrencilerinizin quiz sonuçlarını takip edebilirsiniz.",
      highlight: 'quiz-results'
    },
    {
      title: "📚 Kelime Görevi Oluştur",
      content: "Kelime öğrenme görevleri oluşturup öğrencilerinize gönderebilirsiniz.",
      highlight: 'word-task'
    },
    {
      title: "📈 Kelime Görevleri Sonuçları",
      content: "Kelime görevlerinin sonuçlarını takip edebilirsiniz.",
      highlight: 'word-task-results'
    },
    {
      title: "📅 Ders Programı",
      content: "Ders programınızı yönetebilirsiniz. Günlük bildirimler alabilir kendinize not bırakabilirsiniz.",
      highlight: 'schedule'
    },
    {
      title: "🌐 Çeviri",
      content: "Hızlı çeviri yapabilir ve bu çevirileri hafızalayabilirsiniz.",
      highlight: 'translate'
    },
    {
      title: "🧠 Hafıza",
      content: "Hafızaladığınız bilgilere buradan ulaşabilir günlük bildirimler alabilirsiniz.",
      highlight: 'memory'
    },
    {
      title: "Tebrikler! 🎉",
      content: "Şimdi eğitim paneli butonuna tıklayarak yolumuza devam edelim! Orada benim görselime tıklayarak detaylı bilgi edinebilirsiniz.",
      highlight: null
    }
  ]

  const handleNext = () => {
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(currentStep + 1)
    } else {
      handleClose()
    }
  }

  const handleSkip = () => {
    handleClose()
  }

  const handleClose = () => {
    setOpen(false)
    onBack()
  }

  const getHighlightedCardStyle = (cardId) => {
    if (tutorialSteps[currentStep].highlight === cardId) {
      return {
        transform: 'scale(1.05)',
        boxShadow: '0 8px 25px rgba(0,0,0,0.3)',
        border: '3px solid #1976d2',
        transition: 'all 0.3s ease-in-out'
      }
    }
    return {
      opacity: tutorialSteps[currentStep].highlight ? 0.3 : 1,
      transition: 'all 0.3s ease-in-out'
    }
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onBack}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Ghost Tutorial 👻
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 3 }}>
        {/* Header Card */}
        <Card sx={{ 
          mx: 2, 
          mt: 2, 
          borderRadius: 3,
          boxShadow: 1,
          backgroundColor: '#f8f9fa'
        }}>
          <CardContent sx={{ p: 2.5 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center">
                <Typography variant="h6" sx={{ mr: 2 }}>👻</Typography>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333' }}>
                    {userData?.username || 'Öğretmen'}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Öğretmen
                  </Typography>
                </Box>
              </Box>
              
              <IconButton
                onClick={() => setOpen(true)}
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: '#1976d2',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#1565c0'
                  }
                }}
              >
                <Typography variant="h6">👻</Typography>
              </IconButton>
            </Box>
          </CardContent>
        </Card>

        {/* Banner Ad Placeholder */}
        <Box sx={{ 
          mx: 2, 
          mt: 1.5, 
          height: 60, 
          backgroundColor: '#f0f0f0', 
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#666'
        }}>
          📢 Reklam Alanı
        </Box>

        {/* Menu Items */}
        <Box sx={{ px: 0 }}>
          <Typography variant="h6" sx={{ 
            px: 2, 
            mb: 2, 
            color: '#6E6D6D',
            fontWeight: 'bold',
            fontSize: '20px'
          }}>
            Öğretmen İşlemleri
          </Typography>
          
          {/* Sınıflarım */}
          <Card sx={{ 
            mx: 2, 
            mb: 1, 
            borderRadius: 2,
            ...getHighlightedCardStyle('classes')
          }}>
            <CardContent sx={{ p: 2 }}>
              <Box display="flex" alignItems="center">
                <Typography variant="h6" sx={{ mr: 2, color: '#1976d2' }}>🚪</Typography>
                <Typography variant="h6" fontWeight="bold" color="#333">
                  Sınıflarım
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Öğrenci Soruları */}
          <Card sx={{ 
            mx: 2, 
            mb: 1, 
            borderRadius: 2,
            ...getHighlightedCardStyle('student-questions')
          }}>
            <CardContent sx={{ p: 2 }}>
              <Box display="flex" alignItems="center">
                <Typography variant="h6" sx={{ mr: 2, color: '#9c27b0' }}>❓</Typography>
                <Typography variant="h6" fontWeight="bold" color="#333">
                  Öğrenci Soruları
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Döküman Gönder */}
          <Card sx={{ 
            mx: 2, 
            mb: 1, 
            borderRadius: 2,
            ...getHighlightedCardStyle('send-document')
          }}>
            <CardContent sx={{ p: 2 }}>
              <Box display="flex" alignItems="center">
                <Typography variant="h6" sx={{ mr: 2, color: '#ed6c02' }}>📄</Typography>
                <Typography variant="h6" fontWeight="bold" color="#333">
                  Döküman Gönder
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Mini Quiz Oluştur */}
          <Card sx={{ 
            mx: 2, 
            mb: 1, 
            borderRadius: 2,
            ...getHighlightedCardStyle('mini-quiz')
          }}>
            <CardContent sx={{ p: 2 }}>
              <Box display="flex" alignItems="center">
                <Typography variant="h6" sx={{ mr: 2, color: '#1976d2' }}>📝</Typography>
                <Typography variant="h6" fontWeight="bold" color="#333">
                  Mini Quiz Oluştur
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Quiz Sonuçları */}
          <Card sx={{ 
            mx: 2, 
            mb: 1, 
            borderRadius: 2,
            ...getHighlightedCardStyle('quiz-results')
          }}>
            <CardContent sx={{ p: 2 }}>
              <Box display="flex" alignItems="center">
                <Typography variant="h6" sx={{ mr: 2, color: '#2e7d32' }}>📊</Typography>
                <Typography variant="h6" fontWeight="bold" color="#333">
                  Quiz Sonuçları
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Kelime Görevi Oluştur */}
          <Card sx={{ 
            mx: 2, 
            mb: 1, 
            borderRadius: 2,
            ...getHighlightedCardStyle('word-task')
          }}>
            <CardContent sx={{ p: 2 }}>
              <Box display="flex" alignItems="center">
                <Typography variant="h6" sx={{ mr: 2, color: '#0288d1' }}>📚</Typography>
                <Typography variant="h6" fontWeight="bold" color="#333">
                  Kelime Görevi Oluştur
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Kelime Görevleri Sonuçları */}
          <Card sx={{ 
            mx: 2, 
            mb: 1, 
            borderRadius: 2,
            ...getHighlightedCardStyle('word-task-results')
          }}>
            <CardContent sx={{ p: 2 }}>
              <Box display="flex" alignItems="center">
                <Typography variant="h6" sx={{ mr: 2, color: '#9c27b0' }}>📈</Typography>
                <Typography variant="h6" fontWeight="bold" color="#333">
                  Kelime Görevleri Sonuçları
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Ders Programı */}
          <Card sx={{ 
            mx: 2, 
            mb: 1, 
            borderRadius: 2,
            ...getHighlightedCardStyle('schedule')
          }}>
            <CardContent sx={{ p: 2 }}>
              <Box display="flex" alignItems="center">
                <Typography variant="h6" sx={{ mr: 2, color: '#1976d2' }}>📅</Typography>
                <Typography variant="h6" fontWeight="bold" color="#333">
                  Ders Programı
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Çeviri */}
          <Card sx={{ 
            mx: 2, 
            mb: 1, 
            borderRadius: 2,
            ...getHighlightedCardStyle('translate')
          }}>
            <CardContent sx={{ p: 2 }}>
              <Box display="flex" alignItems="center">
                <Typography variant="h6" sx={{ mr: 2, color: '#9c27b0' }}>🌐</Typography>
                <Typography variant="h6" fontWeight="bold" color="#333">
                  Çeviri
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Hafıza */}
          <Card sx={{ 
            mx: 2, 
            mb: 1, 
            borderRadius: 2,
            ...getHighlightedCardStyle('memory')
          }}>
            <CardContent sx={{ p: 2 }}>
              <Box display="flex" alignItems="center">
                <Typography variant="h6" sx={{ mr: 2, color: '#2e7d32' }}>🧠</Typography>
                <Typography variant="h6" fontWeight="bold" color="#333">
                  Hafıza
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Container>

      {/* Tutorial Dialog */}
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }
        }}
      >
        <DialogContent sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold' }}>
            {tutorialSteps[currentStep].title}
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem', lineHeight: 1.6 }}>
            {tutorialSteps[currentStep].content}
          </Typography>

          {/* Progress Stepper */}
          <Stepper activeStep={currentStep} sx={{ mb: 2 }}>
            {tutorialSteps.map((step, index) => (
              <Step key={index}>
                <StepLabel sx={{ 
                  '& .MuiStepLabel-label': { 
                    color: 'white',
                    fontSize: '0.8rem'
                  }
                }} />
              </Step>
            ))}
          </Stepper>
        </DialogContent>

        <DialogActions sx={{ p: 3, justifyContent: 'space-between' }}>
          <Button
            onClick={handleSkip}
            startIcon={<SkipNextIcon />}
            sx={{ 
              color: 'white',
              border: '1px solid white',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            Atlayalım
          </Button>
          
          <Button
            onClick={handleNext}
            endIcon={currentStep === tutorialSteps.length - 1 ? <CloseIcon /> : <NavigateNextIcon />}
            variant="contained"
            sx={{ 
              backgroundColor: 'white',
              color: '#667eea',
              '&:hover': {
                backgroundColor: '#f5f5f5'
              }
            }}
          >
            {currentStep === tutorialSteps.length - 1 ? 'Bitir' : 'İleri'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default TeacherGhostTutorial
